import { expand } from 'uri-template-lite';
import { apiClient } from '../../common/ApiClient';
import { DataObjectGroupUrl } from './DataObjectGroupUrl';
import { createApiQueryHook } from '../../common/queryClient/createApiQueryHook';
import { BaseDataObjectGroupDto, DataObjectGroupFilter } from '../schema/types';
import {
  AUTOCOMPLETE_PAGE_SIZE,
  OptionsPage,
  OptionType,
  PageRequestParams,
  Sort,
} from '../../common/queryClient';
import { DataObjectGroupPageSchema } from '../schema/DataObjectGroupSchema';
import { QueryFunctionContext } from '@tanstack/react-query';
import { useInfiniteSearchQuery } from '../../common/queryClient/useInfiniteSearchQuery';
import { formatCodeName } from '../../common/utils';
import { DataObjectGroupType } from '../../dataobject/types';

const getDataObjectGroupsQuery = (
  params: DataObjectGroupFilter & PageRequestParams,
) =>
  apiClient
    .get<BaseDataObjectGroupDto[]>(expand(DataObjectGroupUrl.SEARCH, params))
    .then((response) => DataObjectGroupPageSchema.parse(response.data));

export const useDataObjectGroupsQuery = createApiQueryHook(
  ['data-object-groups'],
  getDataObjectGroupsQuery,
);

export interface DataObjectGroupRootOption {
  value: number;
  label: string;
  systemic: boolean;
}

interface DataObjectGroupRootSearchOptions {
  includeSystemic?: boolean;
  selectedGroupIds?: number[];
  sort?: Sort;
}

const searchDataObjectGroupOptions = async <T extends OptionType>(
  { meta, pageParam }: QueryFunctionContext<any, number>,
  mapItem: (item: BaseDataObjectGroupDto) => T,
): Promise<OptionsPage<T>> => {
  const response = await apiClient.get<BaseDataObjectGroupDto[]>(
    expand(DataObjectGroupUrl.SEARCH, {
      searchText: meta?.searchText,
      selectedGroupIds: meta?.selectedGroupIds,
      selectedSubgroupIds: meta?.selectedSubgroupIds,
      groupType: meta?.groupType,
      includeSystemic: meta?.includeSystemic,
      sort: meta?.sort ?? 'code,asc',
      page: pageParam ?? 0,
      size: AUTOCOMPLETE_PAGE_SIZE,
    }),
  );
  const page = DataObjectGroupPageSchema.parse(response.data);

  return {
    content: page.content.map(mapItem),
    last: page.last,
    number: page.number,
  };
};

const searchDataObjectGroupRootOptions = (
  context: QueryFunctionContext<any, number>,
) =>
  searchDataObjectGroupOptions(context, (item) => ({
    value: item.id,
    label: formatCodeName(item),
    systemic: item.systemic,
  }));

export const useInfiniteSearchDataObjectGroupRootsQuery = (
  term: string,
  options?: DataObjectGroupRootSearchOptions,
) =>
  useInfiniteSearchQuery(
    ['data-object-group-roots-autocomplete'],
    searchDataObjectGroupRootOptions,
    term,
    options,
  );

export interface DataObjectGroupTreeOption {
  value: number;
  label: string;
  item: BaseDataObjectGroupDto;
}

export interface DataObjectGroupTreeSearchOptions {
  groupType: DataObjectGroupType;
  selectedGroupIds?: number[];
  selectedSubgroupIds?: number[];
  sort?: Sort;
}

const searchDataObjectGroupTreeOptions = (
  context: QueryFunctionContext<any, number>,
) =>
  searchDataObjectGroupOptions(context, (item) => ({
    value: item.id,
    label: formatCodeName(item),
    item,
  }));

export const useInfiniteSearchDataObjectGroupTreesQuery = (
  term: string,
  options?: DataObjectGroupTreeSearchOptions,
) =>
  useInfiniteSearchQuery(
    ['data-object-group-trees-autocomplete'],
    searchDataObjectGroupTreeOptions,
    term,
    options,
  );
